import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { animate, stagger } from 'animejs';
import Header from './Header';
import { BienestarAgendaFull } from './bienestar/BienestarAgenda';
import { BienestarStoreCta, useBienestarPageMeta } from './bienestar/BienestarShared';
import { HUB_PATHS, navigateTo } from './bienestar/bienestarNav';
import './BienestarPage.css';

const BienestarPage = () => {
  const { t } = useTranslation();
  const pathsRef = useRef(null);

  useBienestarPageMeta('bienestarLandings.metaHubTitle', 'bienestarLandings.metaHubDesc');

  useEffect(() => {
    window.scrollTo(0, 0);
    const hash = window.location.hash?.replace('#', '');
    if (hash) {
      requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, []);

  useEffect(() => {
    const container = pathsRef.current;
    if (!container) return;
    const banners = container.querySelectorAll('.bw-path-banner');
    banners.forEach((b) => {
      b.style.opacity = '0';
      b.style.transform = 'translateY(48px) scale(0.97)';
    });
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animate(banners, {
            opacity: [0, 1],
            translateY: [48, 0],
            scale: [0.97, 1],
            delay: stagger(130),
            duration: 700,
            ease: 'outExpo',
          });
          observer.disconnect();
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bienestar-page bienestar-page--hub">
      <Header />

      <section className="bw-hub-intro">
        <div className="container bw-container">
          <span className="bw-hub-kicker">{t('bienestarLandings.hubKicker')}</span>
          <h1 className="bw-hub-title">{t('bienestarHub.pathsTitle')}</h1>
          <p className="bw-hub-sub">{t('bienestarLandings.hubLead')}</p>
        </div>
      </section>

      <section className="bw-paths" aria-labelledby="bw-paths-title">
        <div className="container bw-container bw-paths-header">
          <h2 id="bw-paths-title" className="bw-paths-title visually-hidden">{t('bienestarHub.pathsTitle')}</h2>
        </div>
        <div className="bw-paths-grid" ref={pathsRef}>
          {HUB_PATHS.map((path) => (
            <div
              key={path.id}
              className="bw-path-banner"
              role="button"
              tabIndex={0}
              onClick={() => navigateTo(path.path)}
              onKeyDown={(e) => e.key === 'Enter' && navigateTo(path.path)}
            >
              <img
                src={path.src}
                alt=""
                loading="lazy"
                onError={(e) => {
                  if (e.currentTarget.src.includes(path.fallbackSrc.split('/').pop())) return;
                  e.currentTarget.src = path.fallbackSrc;
                }}
              />
              <div className="bw-path-banner-overlay">
                <span className="bw-path-tag">{t(`bienestarHub.${path.tagKey}`)}</span>
                <h3 className="bw-path-headline">{t(`bienestarHub.${path.titleKey}`)}</h3>
                <p className="bw-path-desc">{t(`bienestarHub.${path.lineKey}`)}</p>
                <button
                  type="button"
                  className="bw-path-cta"
                  onClick={(e) => { e.stopPropagation(); navigateTo(path.path); }}
                >
                  {t(`bienestarHub.${path.ctaKey}`)}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <main className="bienestar-main bw-main">
        <div className="container bw-container">
          <BienestarStoreCta variant="yoga" />
          <BienestarAgendaFull />
        </div>
      </main>
    </div>
  );
};

export default BienestarPage;
